/**
 * Kasa kodunun QR'ı (gösterme) ve kamerayla okutma — `lib/kasaQr.ts`.
 * QR siyah-beyaz: renk bir yargıdır, bu bir biçim; koyu temada da beyaz
 * zemin (okuyucular açık zemin bekler).
 */

import { M } from "../dil";
import { useEffect, useRef, useState } from "react";
import { kameraAyari, KameraHatasi, qrMatrisi } from "../lib/kasaQr";
import type { Kamera } from "../lib/kasaQr";
import { kanonikKasaKodu, kasaKoduGoster } from "../yedek/hedefler/parafonSunucu";
import { Button, InlineError } from "../ui/primitives";

const KENAR = 4;

export function KasaKoduQr({ kod }: { readonly kod: string }) {
  return <QrResmi metin={kod} etiket={M.yedek.qr.kodQr} testId="kasa-kodu-qr" />;
}

/** Bir metnin QR'ı (siyah-beyaz). `kip="Byte"`: link gibi serbest metin. */
export function QrResmi({
  metin,
  etiket,
  testId,
  kip = "Alphanumeric",
}: {
  readonly metin: string;
  readonly etiket: string;
  readonly testId?: string;
  readonly kip?: "Alphanumeric" | "Byte";
}) {
  const [matris, setMatris] = useState<readonly (readonly boolean[])[] | null>(null);
  const [hata, setHata] = useState<unknown>(undefined);
  useEffect(() => {
    let iptal = false;
    setHata(undefined);
    qrMatrisi(metin, kip).then(
      (m) => {
        if (!iptal) setMatris(m);
      },
      (e: unknown) => {
        if (!iptal) setHata(e);
      },
    );
    return () => {
      iptal = true;
    };
  }, [metin, kip]);
  if (hata !== undefined) return <InlineError error={hata} />;
  if (matris === null) return <p className="text-[12px] text-faint">{M.yedek.qr.hazirlaniyor}</p>;
  const n = matris.length + KENAR * 2;
  let yol = "";
  matris.forEach((satir, r) =>
    satir.forEach((koyu, c) => {
      if (koyu) yol += `M${c + KENAR} ${r + KENAR}h1v1h-1z`;
    }),
  );
  return (
    <svg
      role="img"
      aria-label={etiket}
      data-testid={testId}
      viewBox={`0 0 ${n} ${n}`}
      width={Math.max(160, n * 5)}
      height={Math.max(160, n * 5)}
      shapeRendering="crispEdges"
    >
      <rect width={n} height={n} fill="#ffffff" />
      <path d={yol} fill="#000000" />
    </svg>
  );
}

/**
 * "QR okut": kamera açılır, kareler çözülür; kasa kodu bulununca `onKod`
 * (gösterim biçimiyle) ve kamera HEMEN kapanır. Kasa kodu olmayan QR
 * söylenir, aramaya devam edilir.
 */
export function QrOkut({ onKod }: { readonly onKod: (kod: string) => void }) {
  const [acik, setAcik] = useState(false);
  const [hata, setHata] = useState<unknown>(undefined);
  const [yabanci, setYabanci] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Son geri çağırma: ebeveyn her çizimde yeni işlev verse de kamera yeniden açılmasın.
  const onKodRef = useRef(onKod);
  onKodRef.current = onKod;

  useEffect(() => {
    if (!acik) return;
    let bitti = false;
    let kamera: Kamera | null = null;
    let zaman: ReturnType<typeof setTimeout> | null = null;
    const kapat = (): void => {
      bitti = true;
      if (zaman !== null) clearTimeout(zaman);
      kamera?.kapat();
    };
    const tur = async (): Promise<void> => {
      if (bitti || kamera === null) return;
      const k = kamera.kare();
      if (k !== null) {
        const metin = await kameraAyari.coz(k.veri, k.genislik, k.yukseklik).catch(() => null);
        if (bitti) return;
        if (metin !== null) {
          const kod = kanonikKasaKodu(metin);
          if (kod !== null) {
            kapat();
            setAcik(false);
            onKodRef.current(kasaKoduGoster(kod));
            return;
          }
          setYabanci(true);
        }
      }
      zaman = setTimeout(() => void tur(), kameraAyari.araMs);
    };
    kameraAyari.ac().then(
      (k) => {
        if (bitti) {
          k.kapat();
          return;
        }
        kamera = k;
        if (videoRef.current !== null && k.akis !== null) {
          videoRef.current.srcObject = k.akis;
          void videoRef.current.play?.()?.catch(() => undefined);
        }
        void tur();
      },
      (e: unknown) => {
        if (bitti) return;
        setHata(e instanceof KameraHatasi ? e : new KameraHatasi(M.yedek.qr.kameraAcilamadi));
        setAcik(false);
      },
    );
    return kapat;
  }, [acik]);

  return (
    <div className="space-y-2">
      {!acik ? (
        <Button
          onClick={() => {
            setHata(undefined);
            setYabanci(false);
            setAcik(true);
          }}
        >
          {M.yedek.qr.okut}
        </Button>
      ) : (
        <div className="space-y-2">
          <video ref={videoRef} muted playsInline className="max-h-64 w-full rounded-pf bg-black" aria-label={M.yedek.qr.kameraGoruntusu} />
          <p className="text-[12px] text-muted">{M.yedek.qr.cerceveyeTutun}</p>
          {yabanci && <p className="text-[12px] text-warn">{M.yedek.qr.yabanci}</p>}
          <Button onClick={() => setAcik(false)}>{M.yedek.qr.kameraKapat}</Button>
        </div>
      )}
      {hata !== undefined && <InlineError error={hata} />}
    </div>
  );
}
